import { FC, useState } from 'react';

import { Note, SecondaryAction } from './ScanResultComponent.styles';
import { canUndoCheckIn } from 'utils/utils';
import { haptic } from './haptics';
import { undoCheckIn } from './services';

interface UndoCheckInButtonProps {
	ticketIds: number[];
	eventId: number;
	onDone?: () => void;
}

const serverMessage = (error: unknown) =>
	(error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Could not undo the check-in';

/* Two taps on purpose: the first arms it, the second undoes, so a stray tap at the door changes nothing. */
export const UndoCheckInButton: FC<UndoCheckInButtonProps> = ({ ticketIds, eventId, onDone }) => {
	const [state, setState] = useState<'idle' | 'armed' | 'working' | 'done'>('idle');
	const [error, setError] = useState('');

	if (ticketIds.length === 0 || !canUndoCheckIn(localStorage.getItem('accessToken'))) return null;
	if (state === 'done') return <Note role="status">Check-in undone. The ticket can be scanned again.</Note>;

	const onClick = async () => {
		if (state === 'idle') {
			haptic('warning');
			setState('armed');
			return;
		}
		setState('working');
		setError('');
		try {
			for (const ticketId of ticketIds) {
				await undoCheckIn({ ticketId, eventID: eventId });
			}
			haptic('success');
			setState('done');
			onDone?.();
		} catch (err) {
			haptic('error');
			setError(serverMessage(err));
			setState('idle');
		}
	};

	const label =
		state === 'armed' ? 'Tap again to undo the check-in' : state === 'working' ? 'Undoing…' : 'Undo check-in';
	return (
		<>
			<SecondaryAction type="button" onClick={onClick} disabled={state === 'working'} $armed={state === 'armed'}>
				{label}
			</SecondaryAction>
			{error && (
				<Note role="alert" $error>
					{error}
				</Note>
			)}
		</>
	);
};
